import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  split,
  Operation,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { addBreadcrumb } from '@sentry/react';
import DebounceLink from 'apollo-link-debounce';
import { createClient } from 'graphql-ws';
import unfetch from 'unfetch';
import { loggerLink } from '~/utils/apollo-logger-link';
import { API_URL, WEBSOCKET_URL, IS_PRODUCTION } from '~/utils/constants';
import { store } from '~/utils/store';
import { trackError } from './utils/tracking';
// Fetch polyfill

function opShowingFetch(uri, options) {
  const { operationName } = JSON.parse(options.body);

  return unfetch(`${uri}?op=${operationName}`, options);
}

const isSubscription = ({ query }: Operation) => {
  const definition = getMainDefinition(query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

// Expected business states, not failures. errorLink reports at the Link-chain layer no matter
// how the calling code handles the rejection, so a state the UI already explains to the user
// has to be skipped here or Sentry receives a duplicate report of an expected outcome.
const EXPECTED_GRAPHQL_ERRORS = [
  // Resubmitting the billing address form onto an address that's already saved isn't a real
  // failure - the address is already set the way the user wants. (SUBEXP-2939)
  'Addressable should be unique for the addressable type and address type',
  // Both phrasings the API returns for a duplicate email. error-message.component.tsx already
  // turns either into "That email already exists in our system." (SUBEXP-3014)
  'Email is already taken',
  'Email has already been taken',
];

const isExpectedGraphQLError = (message: string) =>
  EXPECTED_GRAPHQL_ERRORS.some(phrase => message.includes(phrase));

// Some networkError.message values are the literal string "undefined" rather than a real
// falsy value (e.g. `String(undefined)` getting assigned somewhere upstream), which still
// passes a plain truthiness check and reports as content-free noise. (SUBEXP-2936)
const hasNoMessageContent = (message?: string) => !message || message === 'undefined';

const parseApolloErrors = (errorResponse: ErrorResponse) => {
  const { graphQLErrors, networkError } = errorResponse;
  const messages: string[] = [];
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path }) => {
      if (isExpectedGraphQLError(message)) return;
      messages.push(`[GraphQL error]: Message: ${message}, Path: ${path}`);
    });
  }
  if (!hasNoMessageContent(networkError?.message)) {
    messages.push(`[Network error]: ${networkError!.message}`);
  }
  return messages;
};

const httpLink = new HttpLink({
  credentials: 'include',
  headers: {
    Accept: 'application/json',
  },
  uri: `${API_URL}/graphql`,
  // @ts-expect-error - doesn't look like apollo typed this correctly...
  fetch: IS_PRODUCTION ? unfetch : opShowingFetch,
});

const debounceLink = new DebounceLink(100);

const retriable = [
  'failed to fetch',
  'network connection was lost',
  'request timed out',
  'socket hang up',
];

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: error => !!retriable.find(str => str.includes(error?.message?.toLowerCase())),
  },
  delay: {
    initial: 500,
  },
});

const authLink = setContext(() => {
  const token = store.get('TOKEN');
  const authorization = token ? `Bearer ${token}` : '';
  return { headers: { authorization } };
});

const refreshTokenLink = new ApolloLink((operation, forward) => {
  if (isSubscription(operation)) return forward(operation);

  return forward(operation).map(response => {
    const token = operation.getContext()?.response?.headers?.get('x-auth-token');
    if (token) store.set('TOKEN', token);
    return response;
  });
});

const wsLink = new GraphQLWsLink(
  createClient({
    connectionParams: () => ({
      authentication: `Bearer ${store.get('TOKEN')}`,
    }),
    url: `${WEBSOCKET_URL}/graphql`,
  }),
);

const splitLink = split(isSubscription, wsLink, httpLink);

const normalizeError = (message: string) => {
  // this error always comes w/ a unique URL in the message, creating a new Sentry issue each time
  if (message?.includes('socket hang up')) return 'Socket hang up';
  return message;
};

const errorLink = onError((errorResponse: ErrorResponse) => {
  const { operation, networkError } = errorResponse;

  // Raw browser network failures (offline, CORS, etc.) surface as bare Event objects with no
  // real message, so JSON.stringify(networkError) produced content-free noise (`{"isTrusted":true}`).
  // Track it as a breadcrumb instead of a full Sentry error. (SUBEXP-2742, SUBEXP-2936)
  if (networkError && hasNoMessageContent(networkError.message)) {
    addBreadcrumb({
      category: 'network',
      message: 'Network error with no message (likely a connectivity issue)',
      level: 'warning',
    });
  }

  parseApolloErrors(errorResponse).forEach(message => {
    trackError(new Error(normalizeError(message)), { extras: { operation } });
  });
});

const linkArray = [
  debounceLink,
  loggerLink,
  retryLink,
  errorLink,
  refreshTokenLink,
  authLink,
  splitLink,
];

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from(linkArray),
  name: 'FlexFunnel',
});
